// ── Athena Smart Components — auto-detection engine ──────────────────────────
// Reads LLM response + user message to select the right smart card.
// Returns {type, data} or null (null = plain text, no card injected).

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractBullets(block) {
  return (block || '')
    .split('\n')
    .filter(l => /^[\s]*[-*•]\s+/.test(l))
    .map(l => l.replace(/^[\s]*[-*•]\s+/, '').replace(/\*\*/g, '').trim())
    .filter(Boolean);
}

function stripMd(s) {
  return (s || '').replace(/\*\*/g, '').replace(/\*/g, '').trim();
}

// ── Cognitive profile threshold ───────────────────────────────────────────────
// CONVERSACIONAL users get fewer smart cards (they prefer text).
function allowCard(type, cognitiveProfile) {
  const primary = (cognitiveProfile?.traits || [])[0];
  if (primary === 'CONVERSACIONAL') {
    // only high-confidence, high-value cards for conversational users
    return ['checklist', 'decision', 'study', 'recipe'].includes(type);
  }
  return true;
}

// ── 0. Recipe — ingredients + prep steps (Correção 10) ────────────────────────
function detectRecipe(text) {
  const hasIngredients = /(?:\*\*)?ingredientes(?:\*\*)?\s*:?\s*\n/i.test(text);
  const hasPrep = /(?:\*\*)?(?:modo de preparo|preparo|instru[çc][õo]es)(?:\*\*)?\s*:?\s*\n/i.test(text);
  if (!hasIngredients || !hasPrep) return null;

  const nameMatch = text.match(/^#+\s+(.+)$/m) || text.match(/^\*\*(.+?)\*\*\s*$/m);
  const timeMatch = text.match(/(\d+\s*(?:min(?:utos)?|h|horas?))/i);
  const portionsMatch = text.match(/(\d+\s*(?:porç[õo]es|unidades|pessoas?))/i);

  const ingredientsBlock = text.match(/ingredientes\s*:?\s*\n((?:[\s\S]*?))(?=\n\s*\n|\n#{1,3}|\n\*\*(?:modo|preparo|instru)|\n---|$)/i);
  const prepBlock = text.match(/(?:modo de preparo|preparo|instru[çc][õo]es)\s*:?\s*\n((?:[\s\S]*?))(?=\n\s*\n|\n#{1,3}|\n\*\*dica|\n---|$)/i);
  const tipMatch = text.match(/(?:dica|💡)\s*:?\s*(.+?)(?:\n|$)/i);

  const ingredients = ingredientsBlock ? extractBullets(ingredientsBlock[1]) : [];
  if (ingredients.length === 0) return null;

  const stepsRaw = prepBlock ? prepBlock[1] : '';
  const steps = stepsRaw.split('\n')
    .map(l => l.replace(/^[\s]*(?:\d+[.)]|[-*•])\s*/, '').replace(/\*\*/g, '').trim())
    .filter(Boolean);

  return {
    type: 'recipe',
    data: {
      name: nameMatch ? stripMd(nameMatch[1]) : 'Receita',
      time: timeMatch ? timeMatch[1] : null,
      portions: portionsMatch ? portionsMatch[1] : null,
      ingredients,
      steps,
      tip: tipMatch ? stripMd(tipMatch[1]).slice(0, 150) : null,
    },
  };
}

// ── 1. Checklist — markdown task list items ───────────────────────────────────
function detectChecklist(text, umsg) {
  const re = /^[\s]*[-*]\s*\[([x\s ])\]\s+(.+)$/gm;
  const matches = [...text.matchAll(re)];
  if (matches.length < 2) return null;
  return {
    type: 'checklist',
    data: {
      title: 'Tarefas',
      items: matches.map(m => ({ done: m[1].toLowerCase() === 'x', label: stripMd(m[2]) })),
    },
  };
}

// ── 2. Progress — ✓/□ phase patterns ─────────────────────────────────────────
function detectProgress(text) {
  const doneRe = /^[✓✔☑✅]\s+(.+)$/gm;
  const pendRe  = /^[□☐○⬜]\s+(.+)$/gm;
  const done = [...text.matchAll(doneRe)];
  const pend = [...text.matchAll(pendRe)];
  const total = done.length + pend.length;
  if (total < 2) return null;

  const items = [
    ...done.map(m => ({ done: true,  label: stripMd(m[1]) })),
    ...pend.map(m => ({ done: false, label: stripMd(m[1]) })),
  ];
  const pct = Math.round((done.length / total) * 100);

  // Detect title from surrounding context
  const titleMatch = text.match(/^#+\s+(.+)$/m);
  const title = titleMatch ? stripMd(titleMatch[1]) : 'Progresso';

  return { type: 'progress', data: { title, percentage: pct, items } };
}

// ── 3. Decision — Pros/Cons sections ─────────────────────────────────────────
function detectDecision(text) {
  const hasPros = /(\*\*Pro[sz]?\*\*|^#{1,3}\s*Pro[sz]?|Vantagens?)/im.test(text);
  const hasCons = /(\*\*Contras?\*\*|^#{1,3}\s*Contras?|Desvantagens?)/im.test(text);
  if (!hasPros || !hasCons) return null;

  const prosBlock = text.match(/(?:Pro[sz]?|Vantagens?)[^\n]*\n((?:[\s\S]*?))(?=\n#{1,3}|\n\*\*Contra|\n\*\*Desvan|\n---|\n\n\n|$)/i);
  const consBlock = text.match(/(?:Contras?|Desvantagens?)[^\n]*\n((?:[\s\S]*?))(?=\n#{1,3}|\n\*\*Recom|\n---|\n\n\n|$)/i);
  const recMatch  = text.match(/(?:Recomend|Conclus|Resultado)[^\n]*:?\s*(.+)/i);

  const pros = prosBlock ? extractBullets(prosBlock[1]) : [];
  const cons = consBlock ? extractBullets(consBlock[1]) : [];
  if (pros.length === 0 && cons.length === 0) return null;

  return {
    type: 'decision',
    data: {
      title: 'Análise',
      pros,
      cons,
      recommendation: recMatch ? stripMd(recMatch[1]).slice(0, 120) : null,
    },
  };
}

// ── 4. Study — biblical references ───────────────────────────────────────────
const BIBLE_BOOKS_RE = /\b(Gênesis|Êxodo|Levítico|Josué|Salmos|Provérbios|João|Mateus|Lucas|Marcos|Romanos|Filipenses|Colossenses|Efésios|Hebreus|Apocalipse|Atos|Gálatas|Tiago|Gn|Ex|Jo|Mt|Rm|Fp|Hb)\s+\d+:\d+/i;

function detectStudy(text) {
  const refMatch = text.match(BIBLE_BOOKS_RE);
  if (!refMatch) return null;

  const verseMatch = text.match(/"([^"]{15,300})"|«([^«»]{15,300})»/);
  const insightMatch = text.match(/(?:Reflexão|Aplicação|Lição)[:\s]+(.+?)(?:\n|$)/i);

  return {
    type: 'study',
    data: {
      reference: refMatch[0],
      verse: verseMatch ? (verseMatch[1] || verseMatch[2]) : null,
      insight: insightMatch ? stripMd(insightMatch[1]).slice(0, 150) : null,
    },
  };
}

// ── 5. Timeline — dated events ────────────────────────────────────────────────
function detectTimeline(text, umsg) {
  const wantsTimeline = /cronologia|linha do tempo|timeline|histór|evolu[cç]|passo a passo|sequência/i.test(umsg);
  if (!wantsTimeline) return null;

  // Match patterns: "2024 — text" or "**Janeiro 2025**:" or "**Fase 1 (2024):**"
  const evRe = /^[\s]*(?:\*\*)?(\d{4}|\w+\s+\d{4}|Fase\s+\d+|Etapa\s+\d+)(?:\*\*)?[\s:—–-]+(.+)$/gm;
  const events = [...text.matchAll(evRe)];
  if (events.length < 2) return null;

  // Determine which events are "done" based on position (first N-1 treated as done)
  return {
    type: 'timeline',
    data: {
      title: 'Linha do Tempo',
      events: events.map((m, i) => ({
        date: m[1].trim(),
        label: stripMd(m[2]),
        done: i < events.length - 1,
      })),
    },
  };
}

// ── 6. Metrics — labeled percentage bars ─────────────────────────────────────
function detectMetrics(text, umsg) {
  const wantsMetrics = /métrica|indicador|produtividade|saúde|desempenho|performance|pontua[cç]/i.test(umsg);
  if (!wantsMetrics) return null;

  // Match "**LABEL**: 75%" or "LABEL: 75%"
  const re = /\*{0,2}([A-ZÁÉÍÓÚÂ][^:*\n]{1,30})\*{0,2}\s*:\s*(\d{1,3})\s*%/g;
  const matches = [...text.matchAll(re)];
  if (matches.length < 2) return null;

  const COLORS = ['#818cf8','#34d399','#f59e0b','#60a5fa','#f472b6'];
  return {
    type: 'metrics',
    data: {
      title: 'Métricas',
      items: matches.map((m, i) => ({
        label: m[1].trim(),
        value: parseInt(m[2], 10),
        color: COLORS[i % COLORS.length],
      })),
    },
  };
}

// ── 7. Project — project status ───────────────────────────────────────────────
function detectProject(text, umsg) {
  const wantsProject = /status\s+do\s+projeto|progresso\s+do\s+projeto|roadmap|fases\s+do\s+projeto|como\s+(está|tá)\s+o\s+projeto/i.test(umsg);
  if (!wantsProject) return null;

  const phaseMatch   = text.match(/Fase\s+(\d+)/i);
  const totalMatch   = text.match(/(?:de|\/)\s*(\d+)\s*(?:fases|etapas)/i);
  const nextMatch    = text.match(/(?:Próxim[ao]\s+(?:ação|etapa|passo|fase))[:\s]+(.+?)(?:\n|$)/i);
  const statusMatch  = text.match(/(?:Status)[:\s]+(.+?)(?:\n|$)/i);
  const nameMatch    = text.match(/(?:Projeto|Sistema)\s+([A-Z][a-záéíóúâ]+)/);

  if (!phaseMatch) return null;

  const phase = parseInt(phaseMatch[1], 10);
  const total = totalMatch ? parseInt(totalMatch[1], 10) : Math.max(phase + 2, 5);

  return {
    type: 'project',
    data: {
      name: nameMatch ? nameMatch[1] : 'Projeto',
      status: statusMatch ? stripMd(statusMatch[1]).slice(0, 40) : 'Em andamento',
      phase,
      totalPhases: total,
      nextAction: nextMatch ? stripMd(nextMatch[1]).slice(0, 100) : null,
    },
  };
}

// ── Main export ───────────────────────────────────────────────────────────────
export function detectSmartComponent(responseText, userMessage = '', cognitiveProfile = null) {
  if (!responseText || responseText.length < 40) return null;

  const text = responseText;
  const umsg = userMessage.toLowerCase();

  // Run detectors in priority order (most reliable first)
  const detectors = [
    () => detectRecipe(text),
    () => detectChecklist(text, umsg),
    () => detectStudy(text),
    () => detectProgress(text),
    () => detectDecision(text),
    () => detectTimeline(text, umsg),
    () => detectMetrics(text, umsg),
    () => detectProject(text, umsg),
  ];

  for (const detect of detectors) {
    const result = detect();
    if (result && allowCard(result.type, cognitiveProfile)) return result;
  }

  return null;
}
